/**
 * User Preferences Service
 * Manages all user preferences (dark mode, theme, table customizations) in localStorage
 * Structured as JSON that can be sent to backend
 * 
 * For backend integration documentation, see: USER_PREFERENCES_BACKEND.md
 * 
 * This service provides:
 * - Unified storage for all user preferences
 * - Automatic localStorage persistence
 * - JSON structure ready for backend sync
 * - Per-table customization support (column visibility & pinning)
 */

export interface TablePreferences {
  columnVisibility: Record<string, boolean>;
  columnPinning: Record<string, boolean>;
  tableViewMode?: "progress" | "standard"; // Table view mode preference
  viewMode?: "table" | "card"; // View mode preference (table or card)
}

export interface NotificationPreferences {
  // Job Card notifications
  jobCardAssigned: boolean;
  jobCardCompleted: boolean;
  jobCardUpdated: boolean;
  
  // Inventory notifications
  lowStockAlert: boolean;
  outOfStockAlert: boolean;
  stockReceived: boolean;
  
  // Payment notifications
  paymentReceived: boolean;
  paymentPending: boolean;
  invoiceGenerated: boolean;
  
  // Vehicle notifications
  inspectionCompleted: boolean;
  inspectionDue: boolean;
  serviceReminder: boolean;
  vehicleAdded: boolean;
  
  // Estimate notifications
  estimateCreated: boolean;
  estimateApproved: boolean;
  estimateRejected: boolean;
  
  // System notifications
  systemUpdate: boolean;
  maintenanceReminder: boolean;
  
  // QC notifications
  qcApprovalRequired: boolean;
  qcApproved: boolean;
  qcRejected: boolean;
}

export interface UserPreferences {
  userId: string;
  darkMode: boolean;
  theme: "green" | "blue" | "purple" | "orange";
  tablePreferences: Record<string, TablePreferences>;
  notificationPreferences?: NotificationPreferences;
  submoduleShowEditDelete?: boolean; // Global preference for showing Edit/Delete in submodules (default: true)
  lastUpdated: string; // ISO timestamp
}

const USER_PREFERENCES_KEY = "userPreferences";
export const DARK_MODE_STORAGE_KEY = "darkMode";

// Helper function to get user ID
// For localStorage key generation, we use a hash-based approach
// For backend API calls, use getLoggedInUserId() from preferencesSync
const getUserId = (): string => {
  try {
    const encryptedUserData = localStorage.getItem("encryptedUserData") || localStorage.getItem("encryptedData");
    if (encryptedUserData) {
      try {
        const userHash = encryptedUserData.substring(0, 20);
        return `user-${userHash}`;
      } catch {
        // Fallback
      }
    }
  } catch {
    // Fallback
  }
  return "default-user";
};

export const getUserDarkModeKey = (): string => {
  const userId = getUserId();
  return `darkMode-${userId}`;
};

// Get user-specific preferences key
const getUserPreferencesKey = (): string => {
  const userId = getUserId();
  return `${USER_PREFERENCES_KEY}-${userId}`;
};

// Load preferences from localStorage
export const loadUserPreferences = (): UserPreferences | null => {
  try {
    const key = getUserPreferencesKey();
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored) as UserPreferences;
      // Validate structure
      if (parsed && typeof parsed === "object" && parsed.userId && typeof parsed.darkMode === "boolean") {
        return parsed;
      }
    }
  } catch (error) {
    console.warn("[UserPreferences] Failed to load preferences:", error);
  }
  return null;
};

// Save preferences to localStorage
export const saveUserPreferences = (preferences: Partial<UserPreferences>): void => {
  try {
    const key = getUserPreferencesKey();
    const userId = getUserId();

    // Load existing preferences
    const existing = loadUserPreferences();

    // Merge with new preferences
    const updated: UserPreferences = {
      userId,
      darkMode: preferences.darkMode ?? existing?.darkMode ?? false,
      theme: preferences.theme ?? existing?.theme ?? "green",
      tablePreferences: {
        ...existing?.tablePreferences,
        ...preferences.tablePreferences,
      },
      notificationPreferences: preferences.notificationPreferences 
        ? {
            ...getNotificationPreferences(),
            ...preferences.notificationPreferences,
          }
        : existing?.notificationPreferences,
      submoduleShowEditDelete: preferences.submoduleShowEditDelete !== undefined 
        ? preferences.submoduleShowEditDelete 
        : existing?.submoduleShowEditDelete ?? true, // Default: true (enabled)
      lastUpdated: new Date().toISOString(),
    };

    // Save to localStorage
    localStorage.setItem(key, JSON.stringify(updated));

    // Also save to global key for backward compatibility
    localStorage.setItem(USER_PREFERENCES_KEY, JSON.stringify(updated));

    // Auto-sync to backend (debounced) – dynamic import to avoid circular deps
    if (typeof window !== "undefined") {
      import("./preferencesSync")
        .then(({ syncPreferencesToBackend }) => {
          syncPreferencesToBackend();
        })
        .catch((error) => {
          console.warn("[UserPreferences] Failed to schedule backend sync:", error);
        });
    }
  } catch (error) {
    console.warn("[UserPreferences] Failed to save preferences:", error);
  }
};

// Get dark mode preference
export const getDarkModePreference = (): boolean => {
  const prefs = loadUserPreferences();
  if (prefs?.darkMode !== undefined) {
    return prefs.darkMode;
  }

  // Fallback to existing localStorage keys for backward compatibility
  const userId = getUserId();
  const userKey = `darkMode-${userId}`;
  const stored = localStorage.getItem(userKey);
  if (stored !== null) {
    return stored === "true";
  }

  const globalStored = localStorage.getItem("darkMode");
  if (globalStored !== null) {
    return globalStored === "true";
  }

  // Check system preference
  if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return true;
  }

  return false;
};

// Save dark mode preference
export const saveDarkModePreference = (darkMode: boolean): void => {
  saveUserPreferences({ darkMode });

  // Also save to individual keys for backward compatibility
  const userId = getUserId();
  localStorage.setItem(`darkMode-${userId}`, String(darkMode));
  localStorage.setItem("darkMode", String(darkMode));
};

// Get theme preference
export const getThemePreference = (): "green" | "blue" | "purple" | "orange" => {
  const prefs = loadUserPreferences();
  if (prefs?.theme) {
    return prefs.theme;
  }

  // Fallback to existing localStorage keys for backward compatibility
  const userId = getUserId();
  const userKey = `garage360-theme-${userId}`;
  const stored = localStorage.getItem(userKey);
  if (stored && (stored === "green" || stored === "blue" || stored === "purple" || stored === "orange")) {
    return stored as "green" | "blue" | "purple" | "orange";
  }

  const globalStored = localStorage.getItem("garage360-theme");
  if (globalStored && (globalStored === "green" || globalStored === "blue" || globalStored === "purple" || globalStored === "orange")) {
    return globalStored as "green" | "blue" | "purple" | "orange";
  }

  return "green";
};

// Save theme preference
export const saveThemePreference = (theme: "green" | "blue" | "purple" | "orange"): void => {
  saveUserPreferences({ theme });

  // Also save to individual keys for backward compatibility
  const userId = getUserId();
  localStorage.setItem(`garage360-theme-${userId}`, theme);
  localStorage.setItem("garage360-theme", theme);
};

// Get table preferences for a specific table
export const getTablePreferences = (tableId: string): TablePreferences | null => {
  const prefs = loadUserPreferences();
  return prefs?.tablePreferences?.[tableId] || null;
};

// Save table preferences for a specific table
export const saveTablePreferences = (tableId: string, preferences: Partial<TablePreferences>): void => {
  const existing = getTablePreferences(tableId) || {
    columnVisibility: {},
    columnPinning: {},
    tableViewMode: undefined,
    viewMode: undefined,
  };

  const updated: TablePreferences = {
    columnVisibility: {
      ...existing.columnVisibility,
      ...(preferences.columnVisibility || {}),
    },
    columnPinning: {
      ...existing.columnPinning,
      ...(preferences.columnPinning || {}),
    },
    // Only update if provided, otherwise keep existing value
    viewMode: preferences.viewMode !== undefined ? preferences.viewMode : existing.viewMode,
    tableViewMode: preferences.tableViewMode !== undefined ? preferences.tableViewMode : existing.tableViewMode,
  };

  const prefs = loadUserPreferences();
  saveUserPreferences({
    tablePreferences: {
      ...prefs?.tablePreferences,
      [tableId]: updated,
    },
  });
};

// Get all preferences as JSON (for sending to backend)
export const getAllPreferencesAsJSON = (): UserPreferences | null => {
  return loadUserPreferences();
};

// Get all table preferences
export const getAllTablePreferences = (): Record<string, TablePreferences> => {
  const prefs = loadUserPreferences();
  return prefs?.tablePreferences || {};
};

// Get notification preferences with defaults
export const getNotificationPreferences = (): NotificationPreferences => {
  const prefs = loadUserPreferences();
  const defaults: NotificationPreferences = {
    jobCardAssigned: true,
    jobCardCompleted: true,
    jobCardUpdated: true,
    lowStockAlert: true,
    outOfStockAlert: true,
    stockReceived: true,
    paymentReceived: true,
    paymentPending: true,
    invoiceGenerated: true,
    inspectionCompleted: true,
    inspectionDue: true,
    serviceReminder: true,
    vehicleAdded: true,
    estimateCreated: true,
    estimateApproved: true,
    estimateRejected: true,
    systemUpdate: true,
    maintenanceReminder: true,
    qcApprovalRequired: true,
    qcApproved: true,
    qcRejected: true,
  };
  
  if (!prefs?.notificationPreferences) {
    return defaults;
  }
  
  // Merge with defaults to ensure all fields are boolean
  return {
    ...defaults,
    ...prefs.notificationPreferences,
  };
};

// Save notification preferences
export const saveNotificationPreferences = (notificationPrefs: Partial<NotificationPreferences>): void => {
  const existing = getNotificationPreferences();
  saveUserPreferences({
    notificationPreferences: {
      ...existing,
      ...notificationPrefs,
    },
  });
};

// Get submodule Edit/Delete visibility preference
export const getSubmoduleShowEditDelete = (): boolean => {
  const prefs = loadUserPreferences();
  // Default: true (enabled)
  return prefs?.submoduleShowEditDelete ?? true;
};

// Save submodule Edit/Delete visibility preference
export const saveSubmoduleShowEditDelete = (showEditDelete: boolean): void => {
  saveUserPreferences({
    submoduleShowEditDelete: showEditDelete,
  });
};

// Clear table preferences for a specific table (reset column settings)
export const clearTablePreferences = (tableId: string): void => {
  try {
    const prefs = loadUserPreferences();
    if (prefs?.tablePreferences?.[tableId]) {
      // Create a copy without the specific table's preferences
      const updatedTablePrefs = { ...prefs.tablePreferences };
      delete updatedTablePrefs[tableId];
      
      saveUserPreferences({
        tablePreferences: updatedTablePrefs,
      });
    }
  } catch (error) {
    console.warn("[UserPreferences] Failed to clear table preferences:", error);
  }
};

// Clear all preferences
export const clearUserPreferences = (): void => {
  try {
    const key = getUserPreferencesKey();
    localStorage.removeItem(key);
    localStorage.removeItem(USER_PREFERENCES_KEY);
  } catch (error) {
    console.warn("[UserPreferences] Failed to clear preferences:", error);
  }
};
